import { ResourceList, ToggleControl } from "../controls.jsx";
import { GroupHeader, Interaction, OnEndpoints } from "../textRoles.jsx";

export function RemoteManagement({ controls, updateControls, canManage }) {
    return (
        <>
            <GroupHeader title="Remote management" orientation="Whether a session can be driven remotely through Claude Code's Remote Control." keys={["disableRemoteControl"]} />
            <ToggleControl
                disabled={!canManage}
                checked={controls.disableRemoteControl}
                onChange={(checked) => updateControls((value) => ({ ...value, disableRemoteControl: checked }))}
                label="Disable Remote Control"
                description="Blocks device-hosted Remote Control through claude remote-control, claude --remote-control, and startup auto-connect. Cloud-hosted sessions are separate."
            />
            <OnEndpoints written={controls.disableRemoteControl}>
                {controls.disableRemoteControl
                    ? "Remote Control cannot connect from this device: claude remote-control and claude --remote-control are refused, and sessions do not auto-connect."
                    : "No restriction is written. A user may start or connect Remote Control where their version supports it."}
            </OnEndpoints>
        </>
    );
}

export function CrossMachineMessaging({ controls, updateControls, canManage }) {
    return (
        <>
            <GroupHeader title="Cross-machine messaging" orientation="Whether Claude's SendMessage can reach your sessions on other machines without your approval." keys={["isolatePeerMachines"]} />
            <ToggleControl
                disabled={!canManage}
                checked={controls.isolatePeerMachines}
                onChange={(checked) => updateControls((value) => ({ ...value, isolatePeerMachines: checked }))}
                label="Require approval for cross-machine messages"
                description="Claude asks for approval before its SendMessage reaches one of your sessions on another machine."
            />
            <OnEndpoints written={controls.isolatePeerMachines}>
                {controls.isolatePeerMachines
                    ? "A cross-machine SendMessage prompts for approval first, even in bypass permissions mode."
                    : "This policy adds no cross-machine messaging restriction. Other safeguards may still ask for approval."}
            </OnEndpoints>
            <Interaction scope="Any settings file">A true value from any scope turns this on; a checked-in project file can enable it but not disable it.</Interaction>
        </>
    );
}

export function RemoteDevSsh({ controls, updateControls, canManage }) {
    const add = (item) => updateControls((value) => ({
        ...value,
        sshHostAllowlist: value.sshHostAllowlist.includes(item) ? value.sshHostAllowlist : [...value.sshHostAllowlist, item],
    }));
    const remove = (item) => updateControls((value) => ({
        ...value,
        sshHostAllowlist: value.sshHostAllowlist.filter((entry) => entry !== item),
    }));
    return (
        <>
            <GroupHeader title="Remote dev (SSH)" orientation="Limits which hosts a Claude Desktop SSH session can connect to. The terminal CLI ignores this." keys={["sshHostAllowlist"]} />
            <ResourceList
                disabled={!canManage}
                items={controls.sshHostAllowlist}
                onAdd={add}
                onRemove={remove}
                addLabel="Add host"
                inputLabel="SSH host pattern"
                placeholder="*.devboxes.example.com"
                help="Match the resolved hostname after ~/.ssh/config, case-insensitively. Use *.example.com for subdomains; * allows every host."
                example="bastion.example.com"
            />
            {controls.sshHostAllowlist.includes("*") ? <Interaction scope="All hosts">The * pattern removes the host restriction for Desktop SSH sessions.</Interaction> : null}
            <OnEndpoints written={Boolean(controls.sshHostAllowlist.length)}>
                {controls.sshHostAllowlist.length
                    ? "A Claude Desktop SSH session may connect only to a host matching the list."
                    : "No restriction is written. A Desktop SSH session may connect to any host."}
            </OnEndpoints>
        </>
    );
}

export function PublishedOutput({ controls, updateControls, canManage }) {
    return (
        <>
            <GroupHeader title="Published output" orientation="Whether Claude may publish session output as a web page on claude.ai with the Artifact tool." keys={["disableArtifact"]} />
            <ToggleControl
                disabled={!canManage}
                checked={controls.disableArtifact}
                onChange={(checked) => updateControls((value) => ({ ...value, disableArtifact: checked }))}
                label="Disable the Artifact tool"
                description="Stops the Artifact tool from publishing session output as a private web page on claude.ai. Other publishing tools have separate controls."
            />
            <OnEndpoints written={controls.disableArtifact}>
                {controls.disableArtifact
                    ? "The Artifact tool is off. A restrictive value from any settings scope is honored."
                    : "No restriction is written. Claude may publish artifacts to claude.ai."}
            </OnEndpoints>
            {controls.legacyArtifactDisabled && controls.disableArtifact ? <Interaction scope="Imported policy">The draft replaces legacy enableArtifact: false with disableArtifact: true. Review both lines in the draft diff.</Interaction> : null}
        </>
    );
}

export function BrowserControls({ controls, updateControls, canManage }) {
    return (
        <>
            <GroupHeader title="Browser" orientation="What Claude and people may do with external sites in the Claude Desktop Browser pane. The terminal CLI ignores these." keys={["disableBrowserExternalNavigation", "browserExternalPageTools"]} />
            <ToggleControl
                disabled={!canManage}
                checked={controls.disableBrowserExternalNavigation}
                onChange={(checked) => updateControls((value) => ({ ...value, disableBrowserExternalNavigation: checked }))}
                label="Turn off external browsing"
                description="Disables external sites in the Desktop Browser pane for people and Claude. Localhost previews keep working."
            />
            <ToggleControl
                disabled={!canManage}
                checked={controls.browserExternalPageTools}
                onChange={(checked) => updateControls((value) => ({ ...value, browserExternalPageTools: checked }))}
                label="Block Claude's tools on external pages"
                description="People can still open external sites; only Claude's tools are stopped from reading or acting on them."
            />
            <OnEndpoints written={controls.disableBrowserExternalNavigation || controls.browserExternalPageTools}>
                {controls.disableBrowserExternalNavigation
                    ? "External browsing in the Desktop Browser pane is off for people and Claude; localhost previews keep working."
                    : controls.browserExternalPageTools
                        ? "Claude's tools cannot read or act on external pages in the Desktop Browser pane; people can still browse."
                        : "No browser restriction is written."}
            </OnEndpoints>
            {controls.disableBrowserExternalNavigation && controls.browserExternalPageTools ? <Interaction scope="Combined effect">External navigation is already off, so blocking Claude's tools on external pages adds no immediate restriction.</Interaction> : null}
        </>
    );
}
